// ===========================================================================
// Per-message chrome shared by the 1:1 chat and group rooms: the quote block on
// a reply, the Reply / React / Delete controls, the reaction chips, and the
// tombstone left behind when someone takes a message back.
//
// Both screens keep their own message markup -- they look different on purpose
// -- so this module decorates a bubble rather than owning it. Everything here
// works off a message id, which is why ids exist at all: a reply names one, a
// reaction lands on one, and an unsend removes one.
//
// ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { createReactionBar } from './emoji.js';

/** A short opaque id for a message this client is sending. */
export function newMessageId() {
  const bytes = new Uint8Array(9);
  (globalThis.crypto || window.crypto).getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The quoted block that sits above a reply. */
export function renderQuote(replyTo) {
  if (!replyTo || !replyTo.text) return null;
  return el('div', { class: 'msg-quote' }, [
    el('span', { class: 'msg-quote-who', text: replyTo.who || 'Message' }),
    el('span', { class: 'msg-quote-text', text: replyTo.text }),
  ]);
}

/**
 * Decorate a message bubble with its id, actions, and a place for reactions.
 *
 * @param {HTMLElement} node the bubble
 * @param {object} opts
 * @param {string} opts.id message id
 * @param {boolean} opts.mine whether this client sent it
 * @param {string} opts.text plain text, for quoting (empty for a file)
 * @param {string} opts.who display name of the author, for the quote label
 * @param {(quote: {id,text,who}) => void} [opts.onReply]
 * @param {(id: string, emoji: string) => void} [opts.onReact]
 * @param {(id: string) => void} [opts.onDelete]
 */
export function attachMessageActions(node, opts) {
  const { id, mine, text = '', who = '', onReply, onReact, onDelete } = opts;
  if (!id) return; // a message with no id cannot be replied to, reacted to, or unsent
  node.dataset.mid = id;
  node.dataset.mine = mine ? '1' : '';
  node._reactions = new Map(); // emoji -> Set of who reacted

  const reactions = el('div', { class: 'msg-reactions' });
  const bar = createReactionBar((emoji) => {
    bar.classList.add('hidden');
    onReact?.(id, emoji);
  });
  bar.classList.add('hidden');

  const actions = el('div', { class: 'msg-actions' }, [
    onReply
      ? el('button', {
          class: 'msg-act',
          title: t('Reply to this message'),
          text: t('Reply'),
          onClick: (e) => {
            e.stopPropagation();
            onReply({ id, text: text || 'Image', who });
          },
        })
      : null,
    onReact
      ? el('button', {
          class: 'msg-act',
          title: t('React to this message'),
          text: t('React'),
          onClick: (e) => {
            e.stopPropagation();
            bar.classList.toggle('hidden');
            // The chooser opens BELOW the message. On the last message that is
            // below the fold of the transcript, and on a phone the transcript
            // is a few lines tall -- so it opened where nobody could see it,
            // and "React" looked like a button that did nothing.
            if (!bar.classList.contains('hidden')) bar.scrollIntoView({ block: 'nearest' });
          },
        })
      : null,
    // Only your own. The server stamps the sender on an unsend, and a client
    // only removes a message whose author matches -- so this is a convenience,
    // not the control.
    mine && onDelete
      ? el('button', {
          class: 'msg-act danger',
          title: t('Delete this message for everyone'),
          text: t('Delete'),
          onClick: (e) => {
            e.stopPropagation();
            onDelete(id);
          },
        })
      : null,
  ].filter(Boolean));

  node.append(actions, bar, reactions);
}

/**
 * Toggle one person's reaction on a message and redraw its chips.
 * @param {HTMLElement} list the message container
 * @param {string} id message id
 * @param {string} emoji
 * @param {string} who reactor identity ('me' or a peer id)
 * @param {boolean} on
 */
/**
 * Read a set of reactors as a sentence: "You and Katie", "Ana, Ben and 2 more".
 *
 * You come first, because the first thing anyone checks is whether their own
 * reaction registered. Long lists are truncated rather than spilling off the
 * screen -- a tooltip listing eleven names is not a tooltip.
 */
function reactorList(people, nameOf) {
  const names = [];
  if (people.has('me')) names.push('You');
  for (const p of people) {
    if (p === 'me') continue;
    // Somebody the room does not list -- a member who has since left, or an
    // invisible staff member, whose presence is not this tooltip's to give away.
    names.push((nameOf && nameOf(p)) || 'Someone');
  }
  const MAX = 4;
  if (names.length > MAX) {
    const rest = names.length - MAX;
    return `${names.slice(0, MAX).join(', ')} and ${rest} more`;
  }
  if (names.length > 1) return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return names[0] || '';
}

/**
 * @param {Function} [nameOf] peerId -> display name, for the "who reacted"
 *        tooltip. Without it the tooltip falls back to a count.
 */
export function applyReaction(list, id, emoji, who, on, nameOf) {
  const node = list.querySelector(`[data-mid="${cssEscape(id)}"]`);
  if (!node || !node._reactions) return;
  const set = node._reactions.get(emoji) || new Set();
  if (on) set.add(who);
  else set.delete(who);
  if (set.size) node._reactions.set(emoji, set);
  else node._reactions.delete(emoji);

  const box = node.querySelector('.msg-reactions');
  if (!box) return;
  box.replaceChildren();
  for (const [e, people] of node._reactions) {
    box.append(
      el('span', {
        class: `msg-reaction${people.has('me') ? ' mine' : ''}`,
        title: `${reactorList(people, nameOf) || `${people.size}`} reacted${
          people.has('me') ? ': click yours to remove' : ''
        }`,
        text: people.size > 1 ? `${e} ${people.size}` : e,
      }),
    );
  }
}

/** Whether this client has already reacted with `emoji`. */
export function hasMyReaction(list, id, emoji) {
  const node = list.querySelector(`[data-mid="${cssEscape(id)}"]`);
  return !!node?._reactions?.get(emoji)?.has('me');
}

/**
 * Replace a message with a tombstone. The text is gone, but the fact that
 * something was here is not: silently vanishing messages make a conversation
 * confusing to read and easy to gaslight with.
 */
export function removeMessage(list, id) {
  const node = list.querySelector(`[data-mid="${cssEscape(id)}"]`);
  if (!node) return false;
  node._reactions = new Map();
  node.replaceChildren(el('span', { class: 'msg-deleted', text: t('Message deleted') }));
  node.classList.add('deleted');
  delete node.dataset.mid;
  return true;
}

// Attribute selectors need escaping; ids are base64url so this is belt and
// braces, but a selector built from a remote value should never be trusted raw.
function cssEscape(v) {
  return String(v).replace(/["\\]/g, '\\$&');
}

export default { newMessageId, renderQuote, attachMessageActions, applyReaction, hasMyReaction, removeMessage };

/**
 * Attach a preview card under a message.
 *
 * Text only, by design: a remote thumbnail is fetched by every viewer's
 * browser, which would hand the far site each of their IP addresses for a link
 * one person pasted. The title and description are set as TEXT, never as
 * markup -- they came from somebody else's page.
 */
export function applyLinkPreview(list, id, card) {
  const node = list.querySelector(`[data-mid="${cssEscape(id)}"]`);
  if (!node || !card || !card.title) return;
  if (node.querySelector('.link-card')) return; // one per message
  node.append(
    el('a', {
      class: 'link-card',
      href: card.url,
      target: '_blank',
      rel: 'noreferrer noopener',
    }, [
      el('span', { class: 'link-card-host', text: card.host }),
      el('span', { class: 'link-card-title', text: card.title }),
      card.description ? el('span', { class: 'link-card-desc', text: card.description }) : null,
    ].filter(Boolean)),
  );
}
